import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { APIEndpoints } from "../../../config/apiEndpoint.config";
import { HttpService } from "../http.service";
import { Address } from "../Models/Address";
import { PersonLanguageEnum } from "../Models/enums/PersonLanguageEnum";
import { PersonPersonTypeEnum } from "../Models/enums/PersonPersonTypeEnum";
import { SurveyPhotoTypeEnum } from "../Models/enums/SurveyPhotoTypeEnum";
import { Person } from "../Models/read/Person";
import { Survey } from "../Models/read/Survey";
import { AddNewPersonToBuildingDto } from "../Models/write/AddNewPersonToBuildingDto";
import { ChangeOutsideSurveyPhotoIndexDTO } from "../Models/write/ChangeOutsideSurveyPhotoIndexDto";
import { ChangeOutsideSurveyPhotoRemarksDto } from "../Models/write/ChangeOutsideSurveyPhotoRemarksDto";
import { CompleteSurveyDto } from "../Models/write/CompleteSurveyDto";
import { OutsidePhotoDto } from "../Models/write/OutsidePhotoDto";
import { PhotoObjectDto } from "../Models/write/PhotoObjectDto";
import { RejectSurveyDto } from "../Models/write/RejectSurveyDto";
import { SaveOutsideSurveyUnitInfoDto } from "../Models/write/SaveOutsideSurveyUnitInfoDto";
import { SaveOutsideSurveyMainAddressDto } from "../Models/write/SaveOutsideSurveyMainAddressDto";
import { UpdateContactDto } from "../Models/write/UpdateContactDto";
import {
  SaveValidateOutsideSurveyDto,
  ValidateOutsideSurveyDto,
} from "../Models/write/ValidateSurveyDto";
import { DataConvertingService } from "./data-converting.service";
import { Building } from "../Models/read";

@Injectable({
  providedIn: "root",
})
export class OutsideSurveyService {
  constructor(
    private httpService: HttpService,
    private dataService: DataConvertingService
  ) {}

  getSurveyInfo(surveyId: string): Observable<Survey> {
    return this.httpService.get<Survey>(APIEndpoints.getSurvey + surveyId).pipe(
      map((survey) => {
        survey.building = new Building({
          ...survey.building,
          persons: survey.building.persons.map((person) =>
            this.processPerson(person)
          ),
          addresses: survey.building.addresses.map((address) =>
            Object.assign(new Address(), address)
          ),
        });
        return survey;
      })
    );
  }

  savePhotoTab(data: ChangeOutsideSurveyPhotoRemarksDto) {
    return this.httpService
      .post(APIEndpoints.changeOutsideSurveyPhotoRemarks, data)
      .pipe(map(() => true));
  }

  savePhoto(data: OutsidePhotoDto) {
    const photoData = this.dataService.parseEmbeddedUrlFromBase64(
      data.photoData
    );
    data.photoData = photoData.data;
    data.mimeType = photoData.mimetype;
    if (data.originalPhotoData) {
      const originalPhotoData = this.dataService.parseEmbeddedUrlFromBase64(
        data.originalPhotoData
      );
      data.originalPhotoData = originalPhotoData.data;
    }
    return this.httpService.post(APIEndpoints.addPhotoToOutsideSurvey, data);
  }

  removePhotoById(id: string) {
    return this.httpService.post(APIEndpoints.removePhotoToOutsideSurvey, {
      id,
    });
  }

  removeAllPhotoPointsByPhotoId(id: string) {
    return this.httpService.post(
      APIEndpoints.removeAllPhotoPointsByOutsideSurveyPhotoId,
      { id }
    );
  }

  saveMainAddress(data: SaveOutsideSurveyMainAddressDto) {
    return this.httpService.post(APIEndpoints.updateMainAddress, data);
  }

  saveUnit(data: SaveOutsideSurveyUnitInfoDto) {
    return this.httpService.post(APIEndpoints.changeUnitInfo, data);
  }

  saveTechnical(data) {
    return this.httpService.post(APIEndpoints.updateSurvey, data);
  }

  saveOutsideSurveyEngineer(data) {
    return this.httpService.post(
      APIEndpoints.changeOutsideSurveyEngineer,
      data
    );
  }

  searchContacts(query: string): Observable<Person[]> {
    return this.httpService
      .get<Person[]>(APIEndpoints.getContactInfo + query, false)
      .pipe(
        map((persons) => {
          return persons.map((person) => this.processPerson(person));
        })
      );
  }

  updateContact(data: UpdateContactDto) {
    return this.httpService.post(APIEndpoints.updatePerson, data);
  }

  removeContactFromList(data: { buildingId: string; personId: string }) {
    return this.httpService.post(APIEndpoints.removePersonFromBuilding, data);
  }

  addContactToList(data: { buildingId: string; personId: string }) {
    return this.httpService.post(
      APIEndpoints.addExistingPersonToBuilding,
      data
    );
  }

  saveNewContactToList(data: AddNewPersonToBuildingDto) {
    return this.httpService.post(APIEndpoints.addNewPersonToBuilding, data);
  }

  completeSurvey(data: CompleteSurveyDto) {
    return this.httpService.post(APIEndpoints.completeSurvey, data);
  }
  validateSurvey(data: ValidateOutsideSurveyDto) {
    return this.httpService.post(APIEndpoints.validateSurvey, data);
  }
  saveValidateSurvey(data: SaveValidateOutsideSurveyDto) {
    return this.httpService.post(APIEndpoints.saveValidateSurvey, data);
  }
  rejectSurvey(data: RejectSurveyDto) {
    return this.httpService.post(APIEndpoints.rejectSurvey, data);
  }

  processPerson(person) {
    person.personType = Object.keys(PersonPersonTypeEnum).find(
      (key) => PersonPersonTypeEnum[key] === person.personType
    );
    person.language = Object.keys(PersonLanguageEnum).find(
      (key) => PersonLanguageEnum[key] === person.language
    );
    return person;
  }

  refactorSurveyPhotos(surveyPhotos) {
    const photos = {
      facadePhotos: [],
      detailPhotos: [],
      frontYardPhotos: [],
      solutionDoubtPhotos: [],
    };

    surveyPhotos.forEach((p) => {
      p.photoPoints.sort((a, b) => (a.sequence < b.sequence ? -1 : 1));
      const photo = {
        data: {
          originalUrl: p.originalPhotoData,
          url: p.photoData,
          items: p.photoPoints.map((pp) => {
            return new PhotoObjectDto(pp.metadata);
          }),
          id: p.id,
          type: p.type,
          addressId: p.addressId,
          backgroundImageMetadata: p.backgroundImageMetadata,
        },
        facadeDetailPhotoRemark: p.facadeDetailPhotoRemark,
        greyed: false,
        index: p.index,
      };

      switch (p.type) {
        case SurveyPhotoTypeEnum.Facade:
          photos.facadePhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.Detail:
          photos.detailPhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.FrontYard:
          photos.frontYardPhotos.push(photo);
          break;
        case SurveyPhotoTypeEnum.SolutionDoubt:
          photos.solutionDoubtPhotos.push(photo);
          break;
      }
    });
    Object.keys(photos).forEach((k) => {
      photos[k].sort((p1: { index: number }, p2: { index: number }) => {
        if (p1.index > p2.index) return 1;
        else return -1;
      });
    });
    return photos;
  }

  changeOutsideSurveyPhotoIndex(data: ChangeOutsideSurveyPhotoIndexDTO) {
    return this.httpService.post(
      APIEndpoints.changeOutsideSurveyPhotoIndex,
      data
    );
  }
}
